// src/rmaPlantingWindow.js — RMA's OWN planting window, and what it says about a
// seeding. One rule, so the two month-window copies cannot drift again.
//
// ── WHY (Kevin, 2026-10-12: "Wheat planting should follow RMA insurability rules")
//
// The crop-year boundary for a fall-seeded crop used to be a month window we
// picked, and there were TWO of them:
//
//   fallCropYear  (api/_wheat-progress.js)   months 8-12,  wheat only (by caller)
//   rmaCropYear   (api/_jd.js, 15 sites)     months 9-11,  /WHEAT|TRITICALE/
//
// Measured, that disagreement was live: 1,397 ac of wheat/triticale drilled in
// August or December, which rmaCropYear files a year early, and 7,418 ac of rye,
// which it does not treat as fall-seeded at all (rye is: 89 harvests, 87 carrying
// a yield, every one in July or August, against 7,418 ac seeded Sept-Dec).
//
// RMA publishes the real dates per state x county x commodity x type x practice x
// insurance plan, in the ADM's A00200_Date member. sql/086 stores them. This module
// is the only thing that reads them, and it answers two different questions that the
// month window had tangled together:
//
//   1. WHICH CROP YEAR does this seeding belong to?  -> agronomic, Y+1 for a fall crop
//   2. IS IT INSURABLE for that year, and at what guarantee?  -> RMA's dates
//
// Question 2 is where the money is: past the final planting date a wheat guarantee
// slides ~1%/day (RMA guarantee adjustment type L, capped at M), and past the end of
// the late planting period the acres are not insurable for that crop year at all.
//
// ── RULES, each of which fails silently the other way ───────────────────────────
//
//  1. UNKNOWN STAYS UNKNOWN. No window for the county -> status "unknown", never
//     "timely". A missing row must never read as "planted on time".
//  2. THE LATE PERIOD IS READ, NEVER COMPUTED. NY wheat is 15 days (10-10 -> 10-25);
//     OH and IN are FIVE (10-20 -> 10-25). "FPD + 15" is wrong in two of five states.
//  3. A NULL end-of-late-planting MEANS THERE IS NO LATE PERIOD (area plans), not
//     that it is zero days. Those windows cannot judge a grower's guarantee.
//  4. AMBIGUITY IS REPORTED, NOT RESOLVED. 210 of 6,996 county/type/practice keys
//     carry two plans' windows up to a fortnight apart. Where the type or practice is
//     unknown and the surviving windows disagree, say so and name the range.
//  5. THE BASE PLAN GOVERNS THE GUARANTEE. An area plan (SCO/ECO/ARPI) has no
//     individual late-planting slide and reports the individual plans' end-of-late
//     date as its own final planting date — reading it would move a grower's
//     deadline two weeks later.
//
// No imports: the checker loads and drives this file directly.

// RMA base plan codes — the plans that carry an individual guarantee. Same set
// api/_jd.js and both syncs use for BASE_PLAN_CODES.
const BASE_PLAN_CODES = Object.freeze(["01", "02", "03", "90"]);

// Crops RMA seeds in the autumn for the FOLLOWING year's harvest. Rye is here on
// measurement, not on memory: 7,418 ac seeded Sept-Dec against 203 in spring, and
// 87 of 89 harvests carry a yield in July or August.
const FALL_SEEDED_CROPS = Object.freeze(["WHEAT", "TRITICALE", "RYE", "BARLEY"]);

function isFallSeededCrop(crop) {
  const u = String(crop || "").toUpperCase();
  return FALL_SEEDED_CROPS.some((c) => u.includes(c));
}

// "2026-10-12" / "20261012" / Date -> "2026-10-12", else null. Deliberately strict:
// a half-parsed date that silently became Jan 1 would read as timely for any crop.
function isoDay(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.toISOString().slice(0, 10) : null;
  const s = String(v).trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  if (y < 1900 || y > 2200 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function daysBetween(aIso, bIso) {
  const a = Date.parse(aIso + "T00:00:00Z"), b = Date.parse(bIso + "T00:00:00Z");
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

// ── THE WINDOW FOR ONE PLACE ───────────────────────────────────────────────────
// rows: rma_dates rows already narrowed to one crop year + commodity + state +
// county (the index sql/086 creates). type/practice narrow further where known.
//
// Returns { window, candidates, ambiguous, reason }. `window` is null whenever the
// answer is not single-valued — the caller must not pick one.
function resolveWindow(rows, opts) {
  const o = opts || {};
  const type = o.typeCode == null || o.typeCode === "" ? null : String(o.typeCode);
  const practice = o.practiceCode == null || o.practiceCode === "" ? null : String(o.practiceCode);
  const basePlansOnly = o.basePlansOnly !== false;   // default: the guarantee question

  let cand = (rows || []).filter((r) => r && isoDay(r.final_planting_date));
  if (basePlansOnly) cand = cand.filter((r) => BASE_PLAN_CODES.includes(String(r.insurance_plan_code)));
  if (type != null) cand = cand.filter((r) => String(r.type_code) === type);
  if (practice != null) cand = cand.filter((r) => String(r.practice_code) === practice);

  if (!cand.length) {
    return { window: null, candidates: [], ambiguous: false,
             reason: basePlansOnly
               ? "no base-plan window published for this county/type/practice"
               : "no window published for this county/type/practice" };
  }

  // Distinct windows, not distinct rows: many plans share one window.
  const seen = new Map();
  for (const r of cand) {
    const w = {
      final_planting_date: isoDay(r.final_planting_date),
      extended_final_planting_date: isoDay(r.extended_final_planting_date),
      end_of_late_planting_date: isoDay(r.end_of_late_planting_date),
      earliest_planting_date: isoDay(r.earliest_planting_date),
    };
    const k = [w.final_planting_date, w.extended_final_planting_date,
               w.end_of_late_planting_date, w.earliest_planting_date].join("|");
    if (!seen.has(k)) seen.set(k, w);
  }
  const windows = [...seen.values()];
  if (windows.length === 1) return { window: windows[0], candidates: windows, ambiguous: false, reason: null };

  // NOT RESOLVED. Report the spread so a reader can see how much it matters.
  const fpds = windows.map((w) => w.final_planting_date).sort();
  return {
    window: null, candidates: windows, ambiguous: true,
    reason: `${windows.length} different windows apply here (final planting ${fpds[0]} to ` +
            `${fpds[fpds.length - 1]})${type == null ? "; type not known" : ""}` +
            `${practice == null ? "; practice not known" : ""}`,
  };
}

// ── WHAT THE WINDOW SAYS ABOUT ONE SEEDING ─────────────────────────────────────
// status:
//   "timely"    on or before the final planting date  (full guarantee)
//   "late"      after it, on or before the end of the late planting period
//   "past_late" after the end of the late planting period — NOT insurable for the year
//   "early"     before the earliest planting date, where RMA states one
//   "unknown"   no window, or the date could not be read
//
// days_late and guarantee_slide_pct are only ever set for "late". The slide is RMA's
// 1%/day for each day past the final planting date — reported as a HINT, because the
// stored guarantee comes from the AIP and this module models nothing.
function plantingStatus(seedDate, window) {
  const d = isoDay(seedDate);
  if (!d) {
    return { status: "unknown", reason: "no readable seeding date", days_late: null, guarantee_slide_pct: null };
  }
  if (!window || !window.final_planting_date) {
    return { status: "unknown", reason: "no RMA window for this place", days_late: null,
             guarantee_slide_pct: null, seed_date: d };
  }
  const fpd = window.final_planting_date;
  const out = {
    seed_date: d, final_planting_date: fpd,
    end_of_late_planting_date: window.end_of_late_planting_date || null,
    days_late: null, guarantee_slide_pct: null, reason: null,
  };

  if (window.earliest_planting_date && d < window.earliest_planting_date) {
    return { ...out, status: "early",
             reason: `planted before RMA's earliest planting date (${window.earliest_planting_date})` };
  }
  if (d <= fpd) return { ...out, status: "timely" };

  const late = daysBetween(fpd, d);
  // A NULL end-of-late-planting is "this plan has no late period", not zero days.
  // Calling it past_late would condemn every late planting on an area plan, and
  // calling it timely would hide a real one. It is its own answer.
  if (!window.end_of_late_planting_date) {
    return { ...out, status: "unknown", days_late: late,
             reason: "past the final planting date, and this plan publishes no late planting period" };
  }
  if (d <= window.end_of_late_planting_date) {
    return { ...out, status: "late", days_late: late, guarantee_slide_pct: late,
             reason: `${late} day${late === 1 ? "" : "s"} past the final planting date (${fpd}); ` +
                     `the late planting period runs to ${window.end_of_late_planting_date}` };
  }
  return { ...out, status: "past_late", days_late: late,
           reason: `past the end of the late planting period (${window.end_of_late_planting_date}) — ` +
                   `these acres are not insurable for this crop year as planted` };
}

// ── WHICH CROP YEAR ───────────────────────────────────────────────────────────
// For a fall-seeded crop the crop year is the year of HARVEST, so a seeding in the
// autumn belongs to Y+1. That is agronomy and is not in doubt; what RMA's window
// adds is the BOUNDARY — rather than a month we picked.
//
// windowsFor(cropYear) returns the rma_dates rows for that crop year in that county,
// so the rule can ask "does this date fall inside crop year Y+1's planting window".
// It is a callback because the caller owns the fetch.
//
// Falls back to the agronomic +1 only when no window is available, and SAYS SO —
// `basis` is how a caller tells a sourced answer from an unsourced one.
function cropYearForSeeding(crop, seedDate, windowsFor) {
  const d = isoDay(seedDate);
  if (!d) return { crop_year: null, basis: "unknown", reason: "no readable seeding date" };
  const y = Number(d.slice(0, 4));
  if (!isFallSeededCrop(crop)) return { crop_year: y, basis: "spring_crop", reason: null };

  // A fall seeding is next year's crop if it sits inside next year's planting window.
  for (const cy of [y + 1, y]) {
    const rows = typeof windowsFor === "function" ? windowsFor(cy) : null;
    if (!rows || !rows.length) continue;
    const r = resolveWindow(rows, { basePlansOnly: true });
    const w = r.window || r.candidates[0];
    if (!w || !w.final_planting_date) continue;
    const end = w.end_of_late_planting_date || w.final_planting_date;
    const start = w.earliest_planting_date || null;
    if (d <= end && (!start || d >= start)) {
      return { crop_year: cy, basis: "rma_window", ambiguous: !!r.ambiguous,
               window: w, reason: r.ambiguous ? r.reason : null };
    }
  }
  // No window placed it. A fall-seeded crop drilled in the autumn is still next
  // year's crop agronomically — but the basis says the dates did not source it.
  return { crop_year: y + 1, basis: "fall_crop_unsourced",
           reason: "no RMA planting window covers this date; filed as next year's crop on the " +
                   "agronomic rule that an autumn-seeded small grain is harvested the following summer" };
}

module.exports = {
  BASE_PLAN_CODES, FALL_SEEDED_CROPS,
  isFallSeededCrop, isoDay, daysBetween,
  resolveWindow, plantingStatus, cropYearForSeeding,
};
